import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plug, AlertCircle, Package, Plus, ArrowLeft } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Toast from '../components/ui/Toast';
import ActiveIntegrationCard from '../components/integrations/ActiveIntegrationCard';
import IntegrationOption, { IntegrationProvider } from '../components/integrations/IntegrationOption';
import WooCommerceSetup from '../components/integrations/WooCommerceSetup';
import { api } from '../services/api';
import { WooCommerceSyncStatus } from '../types';

type ViewType = 'list' | 'select-provider' | 'setup';

const POLL_INTERVAL = 3000; // 3 seconds
const MAX_POLL_COUNT = 60; // Max 60 polls (3 minutes)

const IntegrationsPage: React.FC = () => {
  const [view, setView] = useState<ViewType>('list');
  const [selectedProvider, setSelectedProvider] = useState<IntegrationProvider | null>(null);
  const [wooCommerceStatus, setWooCommerceStatus] = useState<WooCommerceSyncStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  
  const pollCountRef = useRef(0);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch WooCommerce status on mount
  const fetchStatus = useCallback(async () => {
    try {
      const status = await api.getWooCommerceStatus();
      setWooCommerceStatus(status);
      return status;
    } catch (error: any) {
      // 422 or config not found means no integration set up yet
      if (error?.code !== 'API__NOT_FOUND' && error?.message !== 'WooCommerce catalog source is not enabled') {
        console.error('Failed to fetch WooCommerce status:', error);
      }
      setWooCommerceStatus(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      await fetchStatus();
      setIsLoading(false);
    };
    loadInitialData();
  }, [fetchStatus]);

  // Polling for sync status
  const startPolling = useCallback(() => {
    pollCountRef.current = 0;
    
    const poll = async () => {
      pollCountRef.current++;
      const status = await fetchStatus();
      
      // Stop polling if:
      // 1. Status is no longer 'running'
      // 2. Max poll count reached
      // 3. Status is null (error)
      if (!status || status.lastSyncStatus !== 'running' || pollCountRef.current >= MAX_POLL_COUNT) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsSyncing(false);
        
        if (status?.lastSyncStatus === 'success') {
          setToast({ message: 'Catalog sync completed successfully!', type: 'success' });
        } else if (status?.lastSyncStatus === 'error') {
          setToast({ message: `Sync failed: ${status.lastSyncError}`, type: 'error' });
        }
      }
    };

    pollIntervalRef.current = setInterval(poll, POLL_INTERVAL);
  }, [fetchStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const handleSaveWooCommerce = async (data: { baseUrl: string; consumerKey: string; consumerSecret: string }) => {
    setIsSaving(true);
    try {
      await api.updateWooCommerceConfig({
        platform: 'woocommerce',
        enabled: true,
        baseUrl: data.baseUrl,
        auth: {
          consumerKey: data.consumerKey,
          consumerSecret: data.consumerSecret,
        },
        sync: {
          mode: 'manual',
          entities: ['products', 'categories'],
          perPage: 100,
          includeDrafts: false,
          includeVariations: false,
          fullResyncOnNextRun: false,
        },
      });
      await fetchStatus();
      setToast({ message: 'WooCommerce integration saved successfully!', type: 'success' });
      setView('list');
      setSelectedProvider(null);
    } catch (error: any) {
      setToast({ message: error?.message || 'Failed to save integration', type: 'error' });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async (): Promise<{ ok: boolean; productCount?: number; error?: string }> => {
    setIsTesting(true);
    try {
      const result = await api.testWooCommerceConnection();
      return result;
    } catch (error: any) {
      return { ok: false, error: error?.message || 'Connection test failed' };
    } finally {
      setIsTesting(false);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await api.triggerWooCommerceSync();
      // Optimistically update status to running
      setWooCommerceStatus(prev => prev ? { ...prev, lastSyncStatus: 'running' } : null);
      startPolling();
    } catch (error: any) {
      setIsSyncing(false);
      setToast({ message: error?.message || 'Failed to trigger sync', type: 'error' });
    }
  };

  const integrationProviders: IntegrationProvider[] = ['woocommerce', 'shopify', 'zoho', 'powerbi'];
  const activeProviders: IntegrationProvider[] = ['woocommerce'];

  const hasActiveIntegrations = wooCommerceStatus?.enabled === true;

  const goToSelectProvider = () => {
    setSelectedProvider(null);
    setView('select-provider');
  };

  const goToList = () => {
    setSelectedProvider(null);
    setView('list');
  };

  const renderMyIntegrations = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!hasActiveIntegrations) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold dark:text-white text-gray-900 mb-2">
            No Active Integrations
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            You haven't set up any integrations yet. Connect your e-commerce platform to sync your catalog.
          </p>
          <button
            onClick={goToSelectProvider}
            className="text-primary font-medium hover:underline"
          >
            Set up your first integration →
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {wooCommerceStatus && (
          <ActiveIntegrationCard
            status={{
              enabled: wooCommerceStatus.enabled,
              baseUrl: wooCommerceStatus.activePlatform === 'woocommerce' ? 'https://your-store.com' : '',
              lastSyncStatus: wooCommerceStatus.lastSyncStatus,
              lastSuccessfulSyncAt: wooCommerceStatus.lastSuccessfulSyncAt,
              lastSyncError: wooCommerceStatus.lastSyncError,
              syncMode: wooCommerceStatus.syncMode,
              entities: wooCommerceStatus.entities,
            }}
            onSync={handleSync}
            isSyncing={isSyncing}
          />
        )}
      </div>
    );
  };

  const renderNewIntegration = () => {
    if (selectedProvider === 'woocommerce') {
      return (
        <Card className="max-w-2xl">
          <WooCommerceSetup
            onSave={handleSaveWooCommerce}
            onTestConnection={handleTestConnection}
            isSaving={isSaving}
            isTesting={isTesting}
          />
        </Card>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold dark:text-white text-gray-900 mb-2">
            Choose an Integration
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Select a platform to connect with your Mentoros dashboard
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {integrationProviders.map(provider => (
            <IntegrationOption
              key={provider}
              provider={provider}
              isActive={activeProviders.includes(provider)}
              isSelected={selectedProvider === provider}
              onSelect={() => {
                setSelectedProvider(provider);
                if (activeProviders.includes(provider)) {
                  setView('setup');
                }
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          {view !== 'list' && (
            <button
              onClick={view === 'setup' ? goToSelectProvider : goToList}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          )}
          <div className="p-2 rounded-lg bg-primary/10">
            <Plug className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold dark:text-white text-gray-900">
              {view === 'list' ? 'Integrations' : view === 'select-provider' ? 'New Integration' : 'Configure Integration'}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {view === 'list'
                ? 'Connect your e-commerce platforms to sync catalog data'
                : view === 'select-provider'
                  ? 'Select a platform to connect'
                  : 'Set up your integration credentials'}
            </p>
          </div>
        </div>

        {view === 'list' && (
          <Button onClick={goToSelectProvider}>
            <Plus className="w-4 h-4 mr-2" />
            New Integration
          </Button>
        )}
      </div>

      {/* Content */}
      {view === 'list' && (
        <Card>
          {renderMyIntegrations()}
        </Card>
      )}

      {view === 'select-provider' && (
        <Card>
          {renderNewIntegration()}
        </Card>
      )}

      {view === 'setup' && renderNewIntegration()}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default IntegrationsPage;
